/**
 * useCompanyList 훅 - 세금계산서 발급 시 회사 목록 관리
 * (company-list/useCompanyList.ts에서 통합됨)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Company, CompanyQuickFormData, FormErrors, CompanyListTranslations } from './types';
import { companiesApi } from '@/lib/api/companies';
import { ApiClientError } from '@/lib/api/client';
import { useAuth } from '@/contexts/AuthContext';
import type { CustomerCompany } from '@/lib/api/types';
import { logger } from '@/utils/logger';

/**
 * API 응답(CustomerCompany)을 프론트엔드 타입(Company)으로 변환
 */
function mapCustomerCompanyToCompany(apiCompany: CustomerCompany): Company {
  return {
    id: String(apiCompany.id),
    name: apiCompany.name,
    rfc: apiCompany.rfc,
    email: apiCompany.email,
    phone: apiCompany.phone,
    address: apiCompany.address,
    city: apiCompany.city,
    state: apiCompany.state,
    postalCode: apiCompany.postal_code,
    taxRegime: apiCompany.tax_regime,
    cfdiUse: apiCompany.cfdi_use,
    isDefault: apiCompany.is_default,
    createdAt: apiCompany.created_at,
  };
}

export function useCompanyList(t: CompanyListTranslations) {
  const { isLoggedIn } = useAuth();
  const isMountedRef = useRef(true);

  const [showAddForm, setShowAddForm] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CompanyQuickFormData>({
    name: '',
    rfc: '',
    address: '',
    email: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // 회사 목록 조회
  const fetchCompanies = useCallback(async () => {
    if (!isLoggedIn) {
      setCompanies([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await companiesApi.getCompanies();
      if (!isMountedRef.current) return;

      if (response.success && response.data) {
        setCompanies(response.data.map(mapCustomerCompanyToCompany));
      } else {
        setCompanies([]);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      // 401 에러는 조용히 처리
      if (err instanceof ApiClientError && err.status === 401) {
        setCompanies([]);
        return;
      }

      logger.error('[useCompanyList] 회사 목록 조회 실패:', err);
      setError(err instanceof Error ? err.message : '회사 목록을 불러오는데 실패했습니다');
      setCompanies([]);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isLoggedIn]);

  // 컴포넌트 마운트 시 회사 목록 조회
  useEffect(() => {
    isMountedRef.current = true;
    fetchCompanies();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchCompanies]);

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.companyRequired;
    }
    if (!formData.rfc.trim()) {
      newErrors.rfc = t.rfcRequired;
    }
    if (!formData.address.trim()) {
      newErrors.address = t.addressRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleSaveCompany = useCallback(async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = await companiesApi.createCompany({
        name: formData.name,
        rfc: formData.rfc.toUpperCase(),
        email: formData.email || undefined,
        address: formData.address,
      });

      if (!isMountedRef.current) return;

      if (response.success && response.data) {
        const newCompany = mapCustomerCompanyToCompany(response.data);
        setCompanies(prev => [...prev, newCompany]);
        setFormData({ name: '', rfc: '', address: '', email: '' });
        setErrors({});
        setShowAddForm(false);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      logger.error('[useCompanyList] 회사 추가 실패:', err);
      if (err instanceof ApiClientError && err.status === 422) {
        // 검증 에러 처리
        setError('입력 정보를 확인해주세요');
      } else {
        setError(err instanceof Error ? err.message : '회사 추가에 실패했습니다');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [formData, validateForm]);

  const handleFormChange = useCallback((field: keyof CompanyQuickFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'rfc' ? value.toUpperCase() : value
    }));
  }, []);

  const handleShowAddForm = useCallback(() => {
    setShowAddForm(true);
    setError(null);
  }, []);

  const handleHideAddForm = useCallback(() => {
    setShowAddForm(false);
    setFormData({ name: '', rfc: '', address: '', email: '' });
    setErrors({});
    setError(null);
  }, []);

  return {
    showAddForm,
    companies,
    formData,
    errors,
    isLoading,
    isSaving,
    error,
    handleSaveCompany,
    handleFormChange,
    handleShowAddForm,
    handleHideAddForm,
    refetch: fetchCompanies,
  };
}
